import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useApiQuery } from '@/hooks/useApiQuery';
import { Link } from 'react-router-dom';
import DocumentDetailModal from '@/pages/documents/components/DocumentDetailModal';
import { getFileUrl } from '@/utils/fileUrl';

interface DashboardStats {
  totalCustomers: number;
  totalKycRequests: number;
  pendingKyc: number;
  approvedKyc: number;
  rejectedKyc: number;
  totalDocuments: number;
}

interface RecentKyc {
  id: number;
  requestNumber: string;
  customerName: string;
  status: string;
  submittedAt: string | null;
  createdAt: string;
  customerId: number;
}

interface RecentDoc {
  id: number;
  fileName: string;
  docType: string;
  fileSize: number;
  verificationStatus: string;
  customerName: string;
  createdAt: string;
}

interface Company {
  id: number;
  name: string;
  slug: string;
  contactEmail: string;
  contactPhone: string;
  isActive: boolean;
  isDeleted: number;
  createdAt: string;
  totalUsers: number;
  totalCustomers: number;
  logoUrl: string | null;
}

function StatCard({ label, value, icon, color, trend }: { label: string; value: number; icon: string; color: string; trend?: string }) {
  return (
    <div className="bg-white rounded-xl border border-stone-100 p-5 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-3">
        <div className={`w-10 h-10 rounded-lg ${color} flex items-center justify-center`}>
          <i className={`${icon} text-white text-lg`} />
        </div>
        {trend && <span className="text-emerald-600 text-xs font-medium">{trend}</span>}
      </div>
      <p className="text-2xl font-bold text-stone-900">{value.toLocaleString()}</p>
      <p className="text-stone-500 text-sm mt-1">{label}</p>
    </div>
  );
}

export default function Dashboard() {
  const { user, hasRole } = useAuth();
  const isSuperAdmin = hasRole(['superadmin']);

  const { data: stats, loading } = useApiQuery<DashboardStats>({ endpoint: '/dashboard/stats' });
  const { data: recentKyc } = useApiQuery<RecentKyc[]>({ endpoint: '/kyc?page=1&limit=5' });
  const { data: recentDocs } = useApiQuery<RecentDoc[]>({ endpoint: '/documents?page=1&limit=5' });
  const { data: companies } = useApiQuery<Company[]>({
    endpoint: '/companies?page=1&limit=5',
    enabled: isSuperAdmin,
  });

  const [detailDoc, setDetailDoc] = useState<number | null>(null);

  const quickActions = [
    { label: 'New Customer', path: '/customers', icon: 'ri-user-add-line', color: 'bg-brand-600' },
    { label: 'New KYC Request', path: '/kyc-requests', icon: 'ri-file-add-line', color: 'bg-emerald-600' },
    { label: 'Upload Document', path: '/documents', icon: 'ri-upload-cloud-line', color: 'bg-sky-600' },
    { label: 'View Reports', path: '/reports', icon: 'ri-bar-chart-2-line', color: 'bg-violet-600' },
  ];

  const statusColors: Record<string, string> = {
    draft: 'bg-stone-100 text-stone-600',
    submitted: 'bg-sky-50 text-sky-700',
    'under review': 'bg-amber-50 text-amber-700',
    approved: 'bg-emerald-50 text-emerald-700',
    rejected: 'bg-red-50 text-red-700',
  };

  const docStatusColors: Record<string, string> = {
    pending: 'bg-amber-50 text-amber-700',
    verified: 'bg-emerald-50 text-emerald-700',
    failed: 'bg-red-50 text-red-700',
  };

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const kycList = recentKyc || [];
  const docList = recentDocs || [];
  const companyList = companies || [];

  return (
    <div className="p-4 md:p-8 min-h-screen">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-stone-900">
          Welcome back, {user?.firstName}
        </h1>
        <p className="text-stone-500 text-sm mt-1">
          Here is what is happening in your {user?.company?.name || 'organization'} today.
        </p>
      </div>

      {/* Stats Grid */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 mb-8">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="bg-white rounded-xl border border-stone-100 p-5 h-32 animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 mb-8">
          <StatCard label="Total Customers" value={stats?.totalCustomers || 0} icon="ri-user-line" color="bg-brand-600" trend="+12%" />
          <StatCard label="KYC Requests" value={stats?.totalKycRequests || 0} icon="ri-file-list-3-line" color="bg-sky-600" />
          <StatCard label="Pending Review" value={stats?.pendingKyc || 0} icon="ri-time-line" color="bg-amber-500" />
          <StatCard label="Approved" value={stats?.approvedKyc || 0} icon="ri-check-double-line" color="bg-emerald-600" trend="+8%" />
          <StatCard label="Rejected" value={stats?.rejectedKyc || 0} icon="ri-close-circle-line" color="bg-red-500" />
        </div>
      )}

      {/* Quick Actions */}
      <div className="mb-8">
        <h2 className="text-lg font-bold text-stone-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {quickActions.map((action) => (
            <Link
              key={action.label}
              to={action.path}
              className="bg-white rounded-xl border border-stone-100 p-5 hover:shadow-md hover:border-brand-200 transition-all flex flex-col items-center text-center gap-3"
            >
              <div className={`w-12 h-12 rounded-xl ${action.color} flex items-center justify-center`}>
                <i className={`${action.icon} text-white text-xl`} />
              </div>
              <span className="text-stone-700 text-sm font-medium">{action.label}</span>
            </Link>
          ))}
        </div>
      </div>

      {/* Recent Companies (superadmin only) */}
      {isSuperAdmin && (
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-stone-900">Recent Companies</h2>
            <Link to="/companies" className="text-brand-600 text-sm font-medium hover:text-brand-700">View all</Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {companyList.length === 0 && (
              <div className="col-span-full bg-white rounded-xl border border-stone-100 p-8 text-center">
                <i className="ri-building-4-line text-stone-300 text-2xl mb-2 w-10 h-10 flex items-center justify-center mx-auto" />
                <p className="text-stone-400 text-sm">No companies found.</p>
              </div>
            )}
            {companyList.map((company) => (
              <Link
                key={company.id}
                to={`/companies/${company.id}`}
                className="bg-white rounded-xl border border-stone-100 p-5 hover:shadow-md hover:border-brand-200 transition-all"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-brand-100 flex items-center justify-center shrink-0 overflow-hidden">
                    {company.logoUrl ? (
                      <img src={getFileUrl(company.logoUrl)} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <span className="font-bold text-xs text-brand-700">{company.name.charAt(0)}</span>
                    )}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-stone-900 truncate">{company.name}</p>
                    <p className="text-stone-400 text-xs">{company.slug}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 text-xs text-stone-500">
                  <span className="flex items-center gap-1">
                    <i className="ri-user-line w-3 h-3 flex items-center justify-center" />
                    {company.totalUsers} users
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-customer-service-line w-3 h-3 flex items-center justify-center" />
                    {company.totalCustomers} customers
                  </span>
                </div>
                <div className="mt-3">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${company.isActive ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
                    {company.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-stone-100 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-stone-900">Recent KYC Requests</h3>
            <Link to="/kyc-requests" className="text-brand-600 text-sm font-medium hover:text-brand-700">View all</Link>
          </div>
          <div className="flex flex-col gap-3">
            {kycList.length === 0 && (
              <div className="text-center py-6">
                <i className="ri-file-list-3-line text-stone-300 text-2xl mb-2" />
                <p className="text-stone-400 text-sm">No recent KYC requests.</p>
              </div>
            )}
            {kycList.map((kyc) => (
              <Link
                key={kyc.id}
                to={`/kyc/${kyc.id}`}
                className="flex items-center gap-3 p-3 rounded-lg hover:bg-stone-50 transition-colors"
              >
                <div className="w-9 h-9 rounded-full bg-stone-100 flex items-center justify-center shrink-0">
                  <i className="ri-user-line text-stone-400 text-sm" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-stone-900 text-sm font-medium truncate">{kyc.requestNumber}</p>
                  <p className="text-stone-400 text-xs">{kyc.customerName}</p>
                </div>
                <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${statusColors[kyc.status] || 'bg-stone-100 text-stone-700'}`}>
                  {kyc.status}
                </span>
              </Link>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-stone-100 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-stone-900">Recent Documents</h3>
            <Link to="/documents" className="text-brand-600 text-sm font-medium hover:text-brand-700">View all</Link>
          </div>
          <div className="flex flex-col gap-3">
            {docList.length === 0 && (
              <div className="text-center py-6">
                <i className="ri-file-line text-stone-300 text-2xl mb-2" />
                <p className="text-stone-400 text-sm">No recent documents.</p>
              </div>
            )}
            {docList.map((doc) => (
              <div
                key={doc.id}
                className="flex items-center gap-3 p-3 rounded-lg hover:bg-stone-50 transition-colors cursor-pointer"
                onClick={() => setDetailDoc(doc.id)}
              >
                <div className="w-9 h-9 rounded-lg bg-stone-100 flex items-center justify-center shrink-0">
                  <i className="ri-file-line text-stone-400 text-sm" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-stone-900 text-sm font-medium truncate">{doc.fileName}</p>
                  <p className="text-stone-400 text-xs">{doc.docType} · {formatSize(doc.fileSize)}</p>
                </div>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${docStatusColors[doc.verificationStatus] || 'bg-stone-100 text-stone-700'}`}>
                  {doc.verificationStatus}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <DocumentDetailModal
        isOpen={!!detailDoc}
        onClose={() => setDetailDoc(null)}
        documentId={detailDoc ?? null}
      />
    </div>
  );
}